import React from 'react';
import { Button, Result, Typography } from 'antd';
import { FallbackProps } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import { isProd } from '../../utils/isProd.util';
import { captureException } from '../../services/sentry.service';
import { useAuthUser } from '../../states/user.atom';
import { useActiveTenant } from '../../states/active-tenant.atom';
const { Text, Title } = Typography;

interface ErrorFallbackProps extends FallbackProps {
  message?: string;
  details?: string;
  icon?: React.ReactNode;
  resetText?: string;
  showResetButton?: boolean;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  message = 'An unexpected error occurred',
  details = 'Please try again later',
  icon,
  resetText = 'Try Again',
  showResetButton = true,
}) => {
  const location = useLocation();
  const { activeUser } = useAuthUser();
  const { activeTenant } = useActiveTenant();

  // Extract meaningful error information
  const errorMessage = error?.statusText || error?.message;
  const errorDetails = error?.error?.stack || error?.data || error?.statusText || error?.toString();

  // Log error to Sentry
  React.useEffect(() => {
    if (error) {
      captureException(error, {
        component: 'ErrorFallback',
        errorInfo: {
          message: errorMessage,
          details: errorDetails,
        },
        tags: {
          currentPage: location.pathname,
          userId: activeUser?.id,
          tenantId: activeTenant?.id,
          currentUrl: window.location.href,
        },
        extra: {
          userEmail: activeUser?.email,
          tenantName: activeTenant?.name,
          userRole: activeUser?.role?.name,
          pageSearch: location.search,
          pageHash: location.hash,
          isSysAdmin: activeUser?.isSysAdmin,
        },
      });
    }
  }, [error, errorMessage, errorDetails, location.pathname, activeUser, activeTenant]);

  console.error('Error: ', error);

  return (
    <Result
      status="error"
      icon={icon}
      title={message}
      subTitle={details}
      extra={[
        showResetButton && (
          <Button type="primary" onClick={resetErrorBoundary}>
            {resetText}
          </Button>
        ),
      ]}
    >
      {!isProd() && (errorDetails || errorMessage) && (
        <div style={{ marginTop: 20, textAlign: 'left' }}>
          <Title level={4} type="danger" style={{ whiteSpace: 'pre-wrap' }}>
            Error Details:
          </Title>
          <br />
          <Title level={5} type="danger" style={{ whiteSpace: 'pre-wrap' }}>
            {errorMessage}
          </Title>
          <br />
          <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>
            {errorDetails}
          </Text>
        </div>
      )}
    </Result>
  );
};

export default ErrorFallback;
